import { memo, type ReactNode, useMemo } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { DNDBoardRootDrop } from '~/components/v2/components/shared/dnd/DNDBoardRootDrop';

export type SubSideNavSectionHeaderIcon = typeof ChevronDownIcon;

export type SubSideNavSectionHeaderProps = {
  actions?: ReactNode;
  alwaysShowActions?: boolean;
  collapsed: boolean;
  icon: SubSideNavSectionHeaderIcon;
  isActionsVisible?: boolean;
  isRootDropTarget?: boolean;
  label: string;
  onToggle: () => void;
  'data-testid'?: string;
};

export const SubSideNavSectionHeader = memo(
  ({
    actions,
    alwaysShowActions = false,
    collapsed,
    'data-testid': testId,
    icon: Icon,
    isActionsVisible = false,
    isRootDropTarget = false,
    label,
    onToggle,
  }: SubSideNavSectionHeaderProps) => {
    const toggleButton = useMemo(
      () => (
        <button
          aria-expanded={!collapsed}
          className={cn(
            'relative flex h-6 w-fit cursor-pointer items-center gap-0 rounded-8 border-none bg-transparent px-1.5 font-medium ads:text-secondary text-12 text-gray-11 outline-none ads:hover:bg-hover-subtle hover:bg-gray-2 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset',
            isRootDropTarget &&
              'data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 ads:data-[dnd-over=true]:data-[dnd-droppable=true]:ring-primary data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-inset',
          )}
          data-testid={testId}
          onClick={onToggle}
          type="button"
        >
          <Icon className="mr-1 ads:text-icon-subtle text-gray-9" size="xs" />
          {label}
          <ChevronDownIcon
            className={cn('ads:text-icon-default text-gray-11 transition-transform', collapsed && '-rotate-90')}
            size="xs"
          />
        </button>
      ),
      [Icon, collapsed, isRootDropTarget, label, onToggle, testId],
    );

    return (
      <div
        className="group/sub-side-nav-section-header flex min-h-6 items-center gap-1 py-1 pr-1"
        data-menu-open={isActionsVisible}
      >
        {isRootDropTarget ? <DNDBoardRootDrop asChild>{toggleButton}</DNDBoardRootDrop> : toggleButton}
        {actions ? (
          <div
            className={cn(
              'ml-auto flex items-center gap-0.5 transition-opacity',
              alwaysShowActions
                ? 'opacity-100'
                : 'opacity-0 group-focus-within/sub-side-nav-section-header:opacity-100 group-hover/sub-side-nav-section-header:opacity-100 group-data-[menu-open=true]/sub-side-nav-section-header:opacity-100',
            )}
          >
            {actions}
          </div>
        ) : null}
      </div>
    );
  },
);

SubSideNavSectionHeader.displayName = 'SubSideNavSectionHeader';
